import { useMemo, useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { prescriptions, prescriptionDetailsById } from "../-data/fixtures"
import { Search, Filter } from "lucide-react"
import { PrescriptionDetailsHeader } from "./PrescriptionDetailsHeader"
import { PatientInfoCard } from "./PatientInfoCard"
import { ClinicalInfoCard } from "./ClinicalInfoCard"
import { MedicationsList } from "./MedicationsList"

export default function PrescriptionHubPage() {
  const [tab, setTab] = useState<"all" | "needs" | "ready">("all")
  const [query, setQuery] = useState("")
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const base = prescriptions.filter((p) => {
      if (tab === "needs") return p.status === "needsReview"
      if (tab === "ready") return p.status === "ready"
      return true
    })
    const q = query.trim().toLowerCase()
    if (!q) return base
    return base.filter(
      (p) =>
        p.code.toLowerCase().includes(q) ||
        p.patientName.toLowerCase().includes(q) ||
        p.medicationSummary.toLowerCase().includes(q)
    )
  }, [tab, query])

  const selected = filtered.find((p) => p.id === selectedId) ?? null
  const details = selected ? prescriptionDetailsById[selected.id] : null

  return (
    <div className="flex flex-col h-full overflow-hidden pt-2">
      <div className="grid grid-cols-2 gap-6 max-[780px]:grid-cols-1 flex-1 min-h-0">
        <div className="flex flex-col h-full min-h-0 pl-1">
          <div className="flex items-center justify-between flex-none">
            <h1 className="text-lg font-semibold">Prescriptions</h1>
            <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
              <TabsList className="h-8 p-[2px] rounded-lg">
                <TabsTrigger value="all" className="px-2 py-0.5 text-xs">All</TabsTrigger>
                <TabsTrigger value="needs" className="px-2 py-0.5 text-xs">Needs Review</TabsTrigger>
                <TabsTrigger value="ready" className="px-2 py-0.5 text-xs">Ready</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="flex items-center gap-2 mt-3 flex-none">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search prescriptions"
                className="pl-8 rounded-xl border"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Button variant="outline" size="sm" disabled>
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </div>

          <div className="mt-3 space-y-3 flex-1 overflow-y-auto p-1">
            {filtered.map((p) => (
              <Card
                key={p.id}
                className={`rounded-xl cursor-pointer transition-all ${selectedId === p.id ? "ring-2 ring-blue-500 shadow-md" : "hover:bg-muted/50"}`}
                onClick={() => setSelectedId(p.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold">{p.code}</div>
                      {p.isNew ? (
                        <span className="inline-block h-2 w-2 rounded-full bg-blue-600" aria-hidden="true" />
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.createdAgoLabel}</div>
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">{p.patientName}</div>
                  <div className="text-sm">{p.medicationSummary}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {p.tags.map((t, i) => (
                      <Badge
                        key={i}
                        variant="secondary"
                        className={`${t === "Critical" || t === "Delivery" ? "bg-black text-white" : ""}`}
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="border-l pl-6 max-[780px]:border-none max-[780px]:pl-0 h-full min-h-0 flex flex-col">
          {!selected ? (
            <div className="h-full w-full grid place-items-center">
              <div className="text-sm text-muted-foreground">← Select a prescription to view details</div>
            </div>
          ) : (
            <>
              <div className="flex-none">
                <PrescriptionDetailsHeader 
                  erxNumber={details?.medicinalInfo?.erxNumber || "N/A"} 
                  onClose={() => setSelectedId(null)}
                />
              </div>
              
              <div className="flex-1 overflow-y-auto pt-4 pr-2">
                <div className="space-y-6">
                  {details?.patientInfo && (
                    <PatientInfoCard info={details.patientInfo} />
                  )}
                  
                  {details?.clinicalInfo && (
                    <ClinicalInfoCard info={details.clinicalInfo} />
                  )}

                  {details?.medications && details.medications.length > 0 && (
                    <MedicationsList medications={details.medications} />
                  )}
                  
                  {!details?.patientInfo && !details?.clinicalInfo && !details?.medications && (
                    <div className="text-sm text-muted-foreground p-4 text-center border rounded-xl border-dashed">
                      No detailed information available for this prescription in fixtures.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}